


// ============================================================
// API LAYER — connects frontend to backend
// ============================================================
export const API_BASE = window.location.origin + "/api";

// PERSIST_LOGIN_v1 (5.2 leichte Version) -- "Angemeldet bleiben":
// localStorage statt sessionStorage wenn sp_remember=1 (überlebt Browser-Schließen).
// Standard bleibt sessionStorage (sichere Default fuer geteilte Geraete).
export const _persistFlag = () => { try { return localStorage.getItem("sp_remember") === "1"; } catch { return false; } };
export const _store = () => _persistFlag() ? localStorage : sessionStorage;

export const api = {
  token: null,
  refreshToken: null,

  setTokens(access, refresh) {
    this.token = access;
    this.refreshToken = refresh;
    const s = _store();
    try {
      // Persistent (localStorage) UND sessionStorage gleichzeitig schreiben,
      // damit die 16 direkten `sessionStorage.getItem("sp_token")`-Calls funktionieren.
      // sessionStorage stirbt beim Browser-Schließen, localStorage überlebt — perfekt.
      sessionStorage.setItem("sp_token", access);
      sessionStorage.setItem("sp_refresh", refresh);
      if (s === localStorage) {
        localStorage.setItem("sp_token", access);
        localStorage.setItem("sp_refresh", refresh);
      } else {
        // Wenn nicht persistent, evtl. alte localStorage-Reste löschen
        localStorage.removeItem("sp_token");
        localStorage.removeItem("sp_refresh");
      }
    } catch(e) {}
  },

  loadTokens() {
    // PERSIST_LOGIN_v1: zuerst localStorage, dann sessionStorage probieren.
    // Falls aus localStorage geladen, in sessionStorage spiegeln, damit die 16
    // direkten `sessionStorage.getItem("sp_token")`-Calls weiter funktionieren.
    try {
      const lsTok = localStorage.getItem("sp_token");
      const lsRef = localStorage.getItem("sp_refresh");
      this.token = lsTok || sessionStorage.getItem("sp_token");
      this.refreshToken = lsRef || sessionStorage.getItem("sp_refresh");
      if (lsTok) {
        sessionStorage.setItem("sp_token", lsTok);
        if (lsRef) sessionStorage.setItem("sp_refresh", lsRef);
      }
    } catch(e) {}
    return !!this.token;
  },

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    try {
      sessionStorage.removeItem("sp_token"); sessionStorage.removeItem("sp_refresh");
      localStorage.removeItem("sp_token"); localStorage.removeItem("sp_refresh");
      localStorage.removeItem("sp_remember");
    } catch(e) {}
  },

  // PERSIST_LOGIN_v1: vom Login-Form aufgerufen — setzt nur das Flag, Tokens kommen danach
  setRemember(flag) {
    try {
      if (flag) localStorage.setItem("sp_remember", "1");
      else localStorage.removeItem("sp_remember");
    } catch(e) {}
  },

  async request(path, options = {}) {
    const headers = { "Content-Type": "application/json", ...options.headers };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;

    try {
      const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

      // Token expired — try refresh
      if (res.status === 401 && this.refreshToken && !options._retried) {
        const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          this.setTokens(data.access_token, this.refreshToken);
          return this.request(path, { ...options, _retried: true });
        }
        this.clearTokens();
        return { ok: false, status: 401, data: { error: "Session abgelaufen" } };
      }

      const data = await res.json();
      return { ok: res.ok, status: res.status, data };
    } catch (err) {
      // Backend not reachable — fall back to mock mode
      console.warn("API not reachable, using mock mode:", err.message);
      return { ok: false, status: 0, data: { error: "Backend nicht erreichbar" }, offline: true };
    }
  },

  // Auth
  async login(email, password) {
    return this.request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  },
  async me() { return this.request("/auth/me"); },

  // Data endpoints
  async getDashboard() { return this.request("/dashboard"); },
  async getEmployees() { return this.request("/employees"); },
  async createEmployee(data) { return this.request("/employees", { method: "POST", body: JSON.stringify(data) }); },
  async updateEmployee(id, data) { return this.request(`/employees/${id}`, { method: "PUT", body: JSON.stringify(data) }); },
  async deleteEmployee(id) { return this.request(`/employees/${id}`, { method: "DELETE" }); },
  async getServices() { return this.request("/services"); },
  async getCustomers() { return this.request("/customers"); },
  async getAppointments(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/appointments${qs ? "?" + qs : ""}`);
  },
  async createAppointment(data) { return this.request("/appointments", { method: "POST", body: JSON.stringify(data) }); },
  async updateAppointment(id, data) { return this.request(`/appointments/${id}`, { method: "PUT", body: JSON.stringify(data) }); },
  async deleteAppointment(id) { return this.request(`/appointments/${id}`, { method: "DELETE" }); },
  async getSalons() { return this.request("/salons"); },
  async getVouchers() { return this.request("/vouchers"); },
  async getAICalls() { return this.request("/ai-calls"); },
  async getShifts() { return this.request("/shifts"); },
  async createShift(data) { return this.request("/shifts", { method: "POST", body: JSON.stringify(data) }); },
  async updateShift(id, data) { return this.request(`/shifts/${id}`, { method: "PUT", body: JSON.stringify(data) }); },
  async deleteShift(id) { return this.request(`/shifts/${id}`, { method: "DELETE" }); },
  async getVacations() { return this.request("/vacations"); },
  async createVacation(data) { return this.request("/vacations", { method: "POST", body: JSON.stringify(data) }); },
  async updateVacation(id, data) { return this.request(`/vacations/${id}`, { method: "PUT", body: JSON.stringify(data) }); }, // VACATION_EDIT_v1
  async deleteVacation(id) { return this.request(`/vacations/${id}`, { method: "DELETE" }); },
  // VACATION_ENTITLEMENT_v1 (23.09.2026) -- Urlaubskonto + Vorschau (gleiche Rechnung wie beim Speichern)
  async getVacationBalance(year) { return this.request(`/vacations/balance?year=${encodeURIComponent(year)}`); },
  // VACATION_CARRYOVER_v1 -- Resturlaub-Uebertrag je Person und Jahr (owner_pin nur mitsenden, wenn verlangt)
  async setVacationCarryover(data) { return this.request("/vacations/carryover", { method: "PUT", body: JSON.stringify(data) }); },
  // KASSE_ABO_v1 -- Shearly Kasse als Zusatz-Abo (Angebot/Status, Anfrage mit Zustimmung)
  async getKasseOffer() { return this.request("/kasse/offer"); },
  async requestKasse(data) { return this.request("/kasse/request", { method: "POST", body: JSON.stringify(data) }); },
  async previewVacation(p) {
    const qs = new URLSearchParams(Object.entries(p || {}).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
    return this.request(`/vacations/preview?${qs}`);
  },
  // OWNER_PIN_v1 (23.09.2026) -- Inhaber-PIN
  async getOwnerPin() { return this.request("/owner/pin"); },
  async setOwnerPin(pin, currentPin) { return this.request("/owner/pin", { method: "POST", body: JSON.stringify(currentPin ? { pin, current_pin: currentPin } : { pin }) }); },
  async forgotOwnerPin() { return this.request("/owner/pin/forgot", { method: "POST", body: "{}" }); },
  async resetOwnerPin(token, pin) { return this.request("/owner/pin/reset", { method: "POST", body: JSON.stringify({ token, pin }) }); },
  async getFeatureFlags() { return this.request("/admin/feature-flags"); },
  async createFeatureFlag(data) { return this.request("/admin/feature-flags", { method: "POST", body: JSON.stringify(data) }); },
  async updateFeatureFlags(flags) { return this.request("/admin/feature-flags", { method: "PUT", body: JSON.stringify({ flags }) }); },
  async deleteFeatureFlag(name) { return this.request("/admin/feature-flags", { method: "DELETE", body: JSON.stringify({ name }) }); },
  async getEnabledFeatures() { return this.request("/features"); },
  async getAdminSalons() { return this.request("/admin/salons"); },
  async getAdminCosts() { return this.request("/admin/costs"); },
    // PRICING_FRONTEND_v1 — Pricing + erweiterte Costs-API
    async getAdminCostsFull(range = "30d", ym = null) {
      const q = new URLSearchParams({ range });
      if (ym) q.set("ym", ym);
      return this.request("/admin/costs/full?" + q.toString());
    },
    async refreshAdminCosts(since_days = 90) {
      return this.request("/admin/costs/refresh", { method: "POST", body: JSON.stringify({ since_days }) });
    },
    async getPlatformCosts(ym) { return this.request("/admin/platform-costs/" + ym); },
    async setPlatformCosts(ym, data) { return this.request("/admin/platform-costs/" + ym, { method: "POST", body: JSON.stringify(data) }); },
    async getPricingPlans() { return this.request("/pricing/plans"); },
    async getAdminPricingPlans() { return this.request("/admin/pricing/plans"); },
    async getProviderBalances() { return this.request("/admin/provider-balances"); }, // PROVIDER_BALANCES_v1
    async updatePricingPlan(slug, data) { return this.request("/admin/pricing/plans/" + slug, { method: "POST", body: JSON.stringify(data) }); },

  // Create / Update
  async createCustomer(data) { return this.request("/customers", { method: "POST", body: JSON.stringify(data) }); },
  async updateCustomer(id, data) { return this.request(`/customers/${id}`, { method: "PUT", body: JSON.stringify(data) }); },
  async createService(data) { return this.request("/services", { method: "POST", body: JSON.stringify(data) }); },
  async updateService(id, data) { return this.request(`/services/${id}`, { method: "PUT", body: JSON.stringify(data) }); },
  async deleteService(id) { return this.request(`/services/${id}`, { method: "DELETE" }); },
  async addCardHistory(customerId, data) { return this.request(`/customers/${customerId}/card-history`, { method: "POST", body: JSON.stringify(data) }); },
  async createVoucher(data) { return this.request("/vouchers", { method: "POST", body: JSON.stringify(data) }); },

  // Badges
  async getBadgeSettings() { return this.request("/badges/settings"); },
  async updateBadgeSettings(data) { return this.request("/badges/settings", { method: "PUT", body: JSON.stringify(data) }); },
  async getBadgeAwards() { return this.request("/badges/awards"); },
  // TIME_TRACKING_v1 -- Zeiterfassung
  async timePunch(payload) { return this.request("/time/punch", { method: "POST", body: JSON.stringify(payload) }); },
  async getTimeStatus(employeeId) { return this.request("/time/status" + (employeeId ? "?employee_id=" + employeeId : "")); },
  async getTimeOverview(date) { return this.request("/time/overview" + (date ? "?date=" + date : "")); },
  async getTimeMonth(employeeId, ym) { return this.request("/time/employee/" + employeeId + "/month" + (ym ? "?ym=" + ym : "")); },
  async timeCorrect(payload) { return this.request("/time/correct", { method: "POST", body: JSON.stringify(payload) }); },
  // IMPERSONATION_UI_v1 - admin impersonation helpers
  async startImpersonation(targetUserId) {
    try { sessionStorage.setItem("sp_admin_token_backup", this.token || ""); } catch(e) {}
    const r = await this.request("/admin/impersonate/" + targetUserId, { method: "POST" });
    if (!r.ok || !r.data || !r.data.access_token) {
      try { sessionStorage.removeItem("sp_admin_token_backup"); } catch(e) {}
      throw new Error((r.data && r.data.error) || "Impersonation fehlgeschlagen");
    }
    this.setTokens(r.data.access_token, this.refreshToken);
    return r.data;
  },
  async exitImpersonation() {
    const r = await this.request("/admin/impersonate/exit", { method: "POST" });
    if (!r.ok || !r.data || !r.data.access_token) {
      throw new Error((r.data && r.data.error) || "Exit fehlgeschlagen");
    }
    this.setTokens(r.data.access_token, this.refreshToken);
    try { sessionStorage.removeItem("sp_admin_token_backup"); } catch(e) {}
    return r.data;
  },
  async calculateBadges() { return this.request("/badges/awards", { method: "POST" }); },
};
